import { useDecision } from '@optimizely/react-sdk'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useEffect, useMemo, useState } from 'react'
import { useTheme } from 'styled-components'

import { LinkCustom } from '@/components/shared/link-custom/LinkCustom'
import { RSSFeedLink } from '@/components/shared/rss-feed-link'
import { SrpPagination } from '@/components/srp'
import { ZeroResultsActionBox } from '@/components/srp/zero-results'
import { SRP_LISTING_LIMIT } from '@/constants/pageSettings'
import { getSearchCategoryFromSearchQuery } from '@/domain/category/getSearchCategoryFromSearchQuery'
import { getLocationFromSearchQuery } from '@/domain/location/getLocationFromSearchQuery'
import { isLocationInAlberta } from '@/domain/location/isLocationInAlberta'
import {
  getCasFillersStartIndex,
  getExtendedRadius,
  hasExtendedRadiusListings,
  isLowInventorySearch,
  isZeroInventorySearch,
} from '@/domain/lowInventory'
import { getCurrentPageFromPagination } from '@/domain/srp/getCurrentPageFromPaginationData'
import { rssFeedUrl } from '@/domain/urls'
import { useGetSearchResultsData } from '@/hooks/srp/useGetSearchResultsData'
import { useSearchListCrossPromoConfig } from '@/hooks/useSearchListCrossPromoConfig'
import { MiniLeaderboardAdSlot } from '@/lib/ads/components/ad-slots'
import { AdSense } from '@/lib/ads/components/adsense'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { getSearchPageType } from '@/lib/ga/events/search'
import { FEATURE_FLAG } from '@/lib/optimizely'
import { type Listing } from '@/types/search'
import { BodyText } from '@/ui/atoms/body-text'
import { Flex } from '@/ui/atoms/flex'
import { HeadlineText } from '@/ui/atoms/headline-text'
import { Spacing } from '@/ui/atoms/spacing'
import { TextLink } from '@/ui/atoms/text-link'
import { isAnyFurnitureCategory, isCarsTrucks } from '@/utils/category/categoryTree'

import { SrpLoading } from '../srp-loading'
import { CasBackfillsPanel } from './CasBackfillsPanel'
import { RenderListingCard } from './RenderListingCard'
import {
  type SearchListContainerProps,
  PaginationRSSFeedLinkContainer,
  SearchListContainer,
  TopAdsContainer,
} from './styled'

export type SearchListProps = {
  extendedRadiusListings: Listing[]
  handleSeeAllTopAds: () => Promise<void>
  totalPageCount: number
  isMobile?: boolean
}

const autosSearchListContainerGap: SearchListContainerProps['gap'] = {
  small: 'defaultSmall',
  xLarge: 'default',
}

export const SearchList = ({
  extendedRadiusListings = [],
  handleSeeAllTopAds,
  totalPageCount,
  isMobile,
}: SearchListProps) => {
  const [showCasBackfills, setShowCasBackfills] = useState(false)
  const { colors, spacing } = useTheme()

  const { t } = useTranslation(['srp', 'common'])
  const { asPath } = useRouter()

  const { data, loadingResults: loading } = useGetSearchResultsData()

  const { pagination, searchQuery, results } = data || {}
  const { topAds = [], organic: listings = [] } = results || {}
  const globalKeyword = searchQuery?.keywords || ''
  const { totalCount: totalListings = 0, offset = 0, limit = SRP_LISTING_LIMIT } = pagination || {}

  const currentPage = getCurrentPageFromPagination({ offset, limit })

  const location = getLocationFromSearchQuery(searchQuery)
  const { category } = getSearchCategoryFromSearchQuery(searchQuery?.category)

  const [hideCasBackfillsToggle] = useDecision(FEATURE_FLAG.CAS_BACKFILL_REDUCTION)

  const [isCasBackFillsToggleEnabled, setIsCasBackFillsToggleEnabled] = useState(false)

  const extendedRadius = useMemo(() => getExtendedRadius(location?.radius), [location?.radius])
  const isZeroSrp = useMemo(() => isZeroInventorySearch(totalListings), [totalListings])
  const isLowSrp = useMemo(() => isLowInventorySearch(totalListings), [totalListings])

  // We are interested in the user's location here, not the location of the listing.
  const isAlbertaSearch = useMemo(
    () => isLocationInAlberta({ id: location.id, streetAddress: location.streetAddress }),
    [location.id, location.streetAddress]
  )
  // We want to check the category the user is searching in, not the category
  // of the listing.
  const isCarsTrucksSearch = useMemo(() => isCarsTrucks(category.id), [category.id])

  const { listingsWithCrossPromotion } = useSearchListCrossPromoConfig(listings, category)

  /**
   * CAS backfills are sequential & occur after organic listings
   * if more than 1, show the first one, hide the rest & display show more CTA
   */
  const casBackfillsStartIndex = useMemo(() => getCasFillersStartIndex(listings), [listings])

  const showCasBackfillsPanel = casBackfillsStartIndex !== -1 && !showCasBackfills

  const searchPageType = useMemo(() => getSearchPageType(globalKeyword), [globalKeyword])

  useEffect(() => {
    if (isLowSrp) {
      trackEvent({ action: searchPageType, label: 'SuggestionBoxLoad' })
    }
  }, [isLowSrp, searchPageType])

  useEffect(() => {
    /**
     * hideCasBackfillsToggle.enabled is giving us an hydration error
     * On SSR it returns false and then true on client and impacts the UI
     * TODO: This is a deeper issue on how we use optimizely and we will be investigating what is the best solution for it
     *
     * currently only hiding the backfills for furniture category
     */
    setIsCasBackFillsToggleEnabled(hideCasBackfillsToggle.enabled)
  }, [hideCasBackfillsToggle.enabled])

  const trackTopAdLearnMoreClick = () => {
    trackEvent({ action: GA_EVENT.TopAdLearnMoreClick })
  }

  return (
    <Flex flexDirection="column" gap={spacing.default}>
      <>
        {loading ? (
          <SrpLoading variant="listing-card" />
        ) : (
          <>
            {isZeroSrp ? (
              <>
                {/* Provincial Top Ads (PTAs) need to display on ZSRPs */}
                {!!topAds.length && (
                  <TopAdsContainer>
                    <Spacing mBottom={spacing.default}>
                      <BodyText size="medium" color={colors.grey.primary}>
                        {t('srp:listing.top_ad.label')}{' '}
                        <LinkCustom
                          aria-label={t('srp:listing.top_ad.learn_more_long_label')}
                          href={t('srp:listing.top_ad.learn_more_link')}
                          hasUnderline
                          onClick={trackTopAdLearnMoreClick}
                          variant="secondary"
                        >
                          {t('common:labels.learn_more')}
                        </LinkCustom>
                      </BodyText>
                    </Spacing>

                    <SearchListContainer
                      data-testid="top-ads-list"
                      gap={isCarsTrucksSearch ? autosSearchListContainerGap : undefined}
                    >
                      {topAds.map((item, index) => (
                        <RenderListingCard
                          key={item.id}
                          item={item}
                          index={index}
                          isTopAd={true}
                          listings={listings}
                          globalKeyword={globalKeyword}
                          isCarsTrucksSearch={isCarsTrucksSearch}
                          isAlbertaSearch={isAlbertaSearch}
                          searchPageType={searchPageType}
                          isMobile={isMobile}
                        />
                      ))}
                    </SearchListContainer>

                    <Spacing mTop={spacing.default}>
                      <TextLink
                        as="button"
                        hasUnderline
                        onClick={handleSeeAllTopAds}
                        variant="secondary"
                        data-testid="see-all-top-ads"
                      >
                        {t('common:labels.see_all')}
                      </TextLink>
                    </Spacing>
                  </TopAdsContainer>
                )}
                <ZeroResultsActionBox />
              </>
            ) : (
              <>
                {!!topAds.length && (
                  <TopAdsContainer>
                    <Spacing mBottom={spacing.default}>
                      <BodyText size="medium" color={colors.grey.primary}>
                        {t('srp:listing.top_ad.label')}{' '}
                        <LinkCustom
                          aria-label={t('srp:listing.top_ad.learn_more_long_label')}
                          href={t('srp:listing.top_ad.learn_more_link')}
                          hasUnderline
                          onClick={trackTopAdLearnMoreClick}
                          variant="secondary"
                        >
                          {t('common:labels.learn_more')}
                        </LinkCustom>
                      </BodyText>
                    </Spacing>

                    <SearchListContainer
                      data-testid="top-ads-list"
                      gap={isCarsTrucksSearch ? autosSearchListContainerGap : undefined}
                    >
                      {topAds.map((item, index) => (
                        <RenderListingCard
                          key={item.id}
                          item={item}
                          index={index}
                          isTopAd={true}
                          listings={listings}
                          globalKeyword={globalKeyword}
                          isCarsTrucksSearch={isCarsTrucksSearch}
                          isAlbertaSearch={isAlbertaSearch}
                          searchPageType={searchPageType}
                          isMobile={isMobile}
                        />
                      ))}
                    </SearchListContainer>

                    <Spacing mTop={spacing.default}>
                      <TextLink
                        as="button"
                        hasUnderline
                        onClick={handleSeeAllTopAds}
                        variant="secondary"
                        data-testid="see-all-top-ads"
                      >
                        {t('common:labels.see_all')}
                      </TextLink>
                    </Spacing>
                  </TopAdsContainer>
                )}

                {!!listings.length && (
                  <>
                    <SearchListContainer
                      data-testid="srp-search-list"
                      gap={isCarsTrucksSearch ? autosSearchListContainerGap : undefined}
                    >
                      {listings.map((item, index) => (
                        <RenderListingCard
                          key={item.id}
                          item={item}
                          index={index}
                          listings={listings}
                          globalKeyword={globalKeyword}
                          isCarsTrucksSearch={isCarsTrucksSearch}
                          isAlbertaSearch={isAlbertaSearch}
                          {...(isAnyFurnitureCategory(category) && isCasBackFillsToggleEnabled
                            ? {
                                casBackfillsStartIndex,
                                isCasBackFillsToggleEnabled,
                                showCasBackfillsPanel,
                              }
                            : {})}
                          isCrossPromotionCard={listingsWithCrossPromotion?.includes(item.id)}
                          searchPageType={searchPageType}
                          isMobile={isMobile}
                        />
                      ))}
                    </SearchListContainer>
                  </>
                )}
              </>
            )}

            {/* display a show more panel for displaying the hidden CAS backfill ads */}
            {isAnyFurnitureCategory(category) &&
              isCasBackFillsToggleEnabled &&
              showCasBackfillsPanel && (
                <CasBackfillsPanel
                  setShowCasBackfills={setShowCasBackfills}
                  searchKeywords={globalKeyword}
                />
              )}

            {isLowSrp && (
              <>
                {hasExtendedRadiusListings(extendedRadiusListings.length, extendedRadius) && (
                  <>
                    <Spacing mTop="3.2rem" mBottom="3.2rem">
                      <HeadlineText color={colors.grey.primary} size="xLarge" weight="medium">
                        {t('srp:extended_listings.title', { extendedRadius })}
                      </HeadlineText>
                    </Spacing>

                    <SearchListContainer
                      data-testid="srp-extended-list"
                      gap={
                        isCarsTrucksSearch
                          ? {
                              small: 'default',
                              xLarge: 'mini',
                            }
                          : undefined
                      }
                    >
                      {extendedRadiusListings.map((item, index) => (
                        <RenderListingCard
                          key={item.id}
                          item={item}
                          index={index}
                          listings={extendedRadiusListings}
                          globalKeyword={globalKeyword}
                          isCarsTrucksSearch={isCarsTrucksSearch}
                          isAlbertaSearch={isAlbertaSearch}
                          searchPageType={searchPageType}
                          isMobile={isMobile}
                        />
                      ))}
                    </SearchListContainer>
                  </>
                )}

                <Spacing mTop="6rem">
                  <ZeroResultsActionBox />
                </Spacing>
              </>
            )}
          </>
        )}
      </>
      {/* Minileaderboard should not appear on zSRP */}
      <AdSense id="AFSBottom">{!!totalListings && <MiniLeaderboardAdSlot />}</AdSense>

      <PaginationRSSFeedLinkContainer justifyContent="center" alignItems="center">
        {totalPageCount > 1 && (
          <SrpPagination selectedIndex={currentPage} totalPageCount={totalPageCount} />
        )}

        {!isZeroSrp && <RSSFeedLink url={rssFeedUrl(asPath)} title={t('srp:listing.rss.title')} />}
      </PaginationRSSFeedLinkContainer>
    </Flex>
  )
}
